import { FC, useEffect, useMemo, useReducer, useState } from 'react';

import { Button, Flex, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
  Line,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import IconWarning from '@/assets/icons/icon-warning.svg?react';
import Card from '@/components/RCard';
import RChartTooltip from '@/components/RGraphicTooltip';
import RSelect from '@/components/RSelect';
import RToggleButtons from '@/components/RToggleButtons';
import RTooltip from '@/components/RTooltip';
import {
  filterReducer,
  initialState,
} from '@/pages/Plants/Form/GenerationProfile/filters/reducer';
import { Plant } from '@/types/plant';
import {
  camelCaseToNormalCase,
  formatNumberWithCommas,
  getYearsInRangeOptions,
} from '@/utils';

type Props = {
  currentPlantId?: string | number;
};

const GenerationCard: FC<Props> = ({ currentPlantId }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [filterParams, dispatch] = useReducer(filterReducer, initialState);

  const { data: plantData } = useSWR<Plant>(
    currentPlantId ? `/plant/${currentPlantId}/` : null,
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (plantData?.startDate && plantData?.endDate) {
      const years = getYearsInRangeOptions(
        plantData.startDate,
        plantData.endDate
      );
      dispatch({
        type: 'SET_YEARS',
        payload: years,
      });
    }
  }, [plantData]);

  const { apiEndpoint, queryString } = useMemo((): {
    apiEndpoint: string | null;
    queryString: string | null;
  } => {
    if (currentPlantId && filterParams.selectedYear) {
      const endpoint = `/plant/${currentPlantId}/generation_graph/`;
      const params: Record<string, string> = {};

      if (filterParams.selectedYear) {
        params.startDatetime = dayjs(
          `${filterParams.selectedYear}-01-01`
        ).toISOString();
        params.endDatetime = dayjs(
          `${filterParams.selectedYear}-12-31`
        ).toISOString();
        params.timeUnit = `month`;
      }

      const queryString = new URLSearchParams(params).toString();

      return {
        apiEndpoint: endpoint,
        queryString,
      };
    }

    return {
      apiEndpoint: null,
      queryString: null,
    };
  }, [filterParams, currentPlantId]);

  const [fullURL, setFullURL] = useState(() => {
    return apiEndpoint ? `${apiEndpoint}?${queryString}` : null;
  });

  useEffect(() => {
    if (apiEndpoint) {
      setFullURL(`${apiEndpoint}?${queryString}`);
    } else {
      setFullURL(null);
    }
  }, [apiEndpoint, queryString]);

  const {
    data: generatedGraphData,
    isLoading: isGraphDataLoading,
    error,
  } = useSWR(fullURL);

  const handleFilterChange = (key: 'selectedYear', value: string | number) => {
    if (key === 'selectedYear') {
      dispatch({
        type: 'SET_SELECTED_YEAR',
        payload: Number(value),
      });
    }
  };

  return (
    <Card
      $minHeight="300px"
      style={{ width: '100%', marginTop: '24px' }}
      $paddingBody="24px 0px"
      title={
        <Flex gap={8} align="center">
          <Typography.Title level={4} className="fs-17-bold">
            {t('components.simReport.generationCard.title')}
          </Typography.Title>
          <RTooltip
            title={t('components.simReport.generationCard.tooltip.title')}
            description={t(
              'components.simReport.generationCard.tooltip.description'
            )}
          >
            <IconInfo />
          </RTooltip>
        </Flex>
      }
      extra={
        filterParams.years.length > 0 && (
          <Flex gap={16} align="center">
            <Flex style={{ height: '32px' }} gap={8} align="center">
              <RToggleButtons
                disabled={isGraphDataLoading}
                height={'auto'}
                labels={[
                  t('components.simReport.generationCard.filters.daily'),
                  t('components.simReport.generationCard.filters.annually'),
                ]}
                selectedLabel={t(
                  'components.simReport.generationCard.filters.annually'
                )}
              />
              <RSelect
                disabled={isGraphDataLoading}
                style={{ height: '100%' }}
                width={120}
                defaultValue={filterParams.selectedYear}
                options={filterParams.years.map((year) => ({
                  label: year.toString(),
                  value: year,
                }))}
                onChange={(value) => handleFilterChange('selectedYear', value)}
              />
            </Flex>
            <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
          </Flex>
        )
      }
    >
      {isGraphDataLoading && (
        <Flex justify="center" align="center" style={{ height: 400 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      )}
      {!isGraphDataLoading && generatedGraphData && error && (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 400 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          {error && (
            <Typography.Text className="fs-14-regular text-gray-color text-center">
              {t('components.simReport.generationCard.error.fetchDataError')}
            </Typography.Text>
          )}
          {!generatedGraphData && (
            <Typography.Text className="fs-14-regular text-gray-color text-center">
              {t('components.simReport.generationCard.error.noDataAvailable')}
            </Typography.Text>
          )}
        </Flex>
      )}
      {!isGraphDataLoading && !generatedGraphData && (
        <Flex vertical style={{ height: 300 }} justify="center" align="center">
          <IconWarning
            fill={theme.colors.tagRevoked}
            className="mb-24"
            width={50}
            height={50}
          />
          <Typography.Text className="fs-14-regular text-gray-color text-center">
            {t('components.simReport.generationCard.uploadMessage')}
          </Typography.Text>
        </Flex>
      )}
      {!isGraphDataLoading && generatedGraphData && (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            margin={{ top: 15, right: 24, left: 0, bottom: 15 }}
            data={generatedGraphData.graphData}
          >
            <XAxis
              angle={-45}
              textAnchor="end"
              interval="preserveStart"
              tickFormatter={(tick) => dayjs(tick).format('MMM')}
              dataKey="dt"
            />
            <YAxis
              tickFormatter={(value) =>
                `${formatNumberWithCommas(Number(value))}`
              }
              interval="preserveEnd"
              dataKey="totalGeneration"
            />
            <Tooltip
              content={({ active, payload, label }) => {
                const typedPayload = payload as Payload<number, string>[];

                const transformedPayload = typedPayload.map((entry) => ({
                  ...entry,
                  name: entry.name && camelCaseToNormalCase(entry.name),
                  unit: 'MWh',
                  color: theme.colors.primary,
                }));
                return (
                  <RChartTooltip
                    active={active}
                    payload={transformedPayload}
                    label={`${dayjs(label).format('MMMM')}, ${dayjs(
                      label
                    ).format('YYYY')}`}
                  />
                );
              }}
            />
            <CartesianGrid
              horizontal={true}
              vertical={false}
              stroke={theme.colors.grayVeryLight}
            />
            <Line
              dot={false}
              type="natural"
              dataKey="totalGeneration"
              stroke={theme.colors.primary}
              strokeWidth={2}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default GenerationCard;
